import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { PropertyComponent } from '../../main/property/property.component';
import { SettingWithMetadata } from '../../models';
import { NotificationService } from '../../main/notification.service';
import { ChargersService } from '../chargers.service';

@Component({
	selector: 'app-charger-settings-user',
	imports: [MatListModule, MatProgressSpinnerModule, MatIconModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatInputModule, PropertyComponent, MatCardModule],
	templateUrl: './charger-settings-user.component.html',
	styleUrl: './charger-settings-user.component.scss'
})
export class ChargerSettingsUserComponent implements OnInit {
	chargerId!: string;
	chargerProperties = signal<SettingWithMetadata[]>([]);
	chargerPropertiesLoaded = signal<boolean>(false);

	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private chargersService = inject(ChargersService);
	private notificationService = inject(NotificationService);
	private destroyRef = inject(DestroyRef);

	ngOnInit() {
		this.chargerId = this.route.parent?.snapshot.paramMap.get('id') ?? '';
		const isValid = /^[PD]\d{14}$/.test(this.chargerId);
		if (!isValid) {
			this.router.navigate(['/404']);
		}

		this.chargersService.getChargerSettingsUser(this.chargerId).pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: (charger) => {
				console.log('getChargerSettingsUser():', charger);

				this.chargerProperties.set([
					{
						id: 'cableSettings',
						name: $localize`:@@CableSetting:Cable Setting`,
						valueWithMetadata: charger.cableSettings,
						type: 'string'
					},
					{
						id: 'chargingMode',
						name: $localize`:@@ChargingMode:Charging Mode`,
						valueWithMetadata: charger.chargingMode,
						type: 'string'
					},
					{
						id: 'minimumChargingCurrent',
						name: $localize`:@@MinimumChargingCurrent:Minimum Charging Current` + ' [A]',
						valueWithMetadata: charger.minimumChargingCurrent,
						type: 'number'
					},
					{
						id: 'maximumChargingCurrent',
						name: $localize`:@@MaximumChargingCurrent:Maximum Charging Current` + ' [A]',
						valueWithMetadata: charger.maximumChargingCurrent,
						type: 'number'
					},
					{
						id: 'startMode',
						name: $localize`:@@StartMode:Start Mode`,
						valueWithMetadata: charger.startMode,
						type: 'string'
					},
				]);
				console.log('getChargerSettingsUser() chargerProperties:', this.chargerProperties());
				this.chargerPropertiesLoaded.set(true);
			},
			error: (err) => {
				console.error('getChargerSettingsUser() error:', err);
				this.notificationService.openSnackbar(false, err.error.errorMessage, $localize`:@@Load:Load`);
				this.chargerPropertiesLoaded.set(true);
			},
		});
	}


	saveCallback = (setting: SettingWithMetadata) => this.saveSetting(setting); // for preserving `this` context in `saveSetting`

	saveSetting(setting: SettingWithMetadata): Observable<boolean> {
		console.log('saveSetting():', setting);
		let value;
		if ((setting.valueWithMetadata) && ('value' in setting.valueWithMetadata)) {
			value = setting.valueWithMetadata?.value;
		}

		if (setting && value !== null) {
			return this.chargersService.putChargerSettingsUser(this.chargerId, setting.id, value).pipe(
				map(result => {
					return this.notificationService.openSnackbar(result.success, result.errorMessage);
				}),
				catchError(err => {
					return of(this.notificationService.openSnackbar(false, err.message));
				})
			);
		}
		console.log('saveSetting() skipped — setting or value invalid:', setting, value);
		return of(false);
	}



}
